import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { Button, Drawer, Stack, Tabs, Tab, Tooltip, useMediaQuery, useTheme, Typography } from '@mui/material'
import { ArrowBack, DynamicFeed } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import { useTransferProjectMutation, useTransferDatesMutation } from '../../store/redux/reducers/worklogSlice'
import { SquareButton } from '../../styles/ButtonStyles'
import DateRangePickerWithPresets from '../DateRangePickerWithPresets/DateRangePickerWithPresets'
import { useForm } from 'react-hook-form'
import { Option } from '../Form/Inputs/SelectInput'
import { defaultPageSize } from '../../config/defaultPagination'
import { useGetAcronymsQuery, ProjectAcronym } from '../../store/redux/reducers/projectsSlice'
import AutocompleteInput from '../Form/Inputs/AutocompleteInput'
import { filterOptions } from '../../store/utils/filterProjectsAcronymsOptions'
import { useAppSelector, useNotification } from '../../store/redux/hooks'
import { createErrorMessage } from '../../store/utils'
import i18n from '../../config/i18n'
import { getSelectedDate } from '../../store/redux/reducers/monthPickerSlice'

type FormValues = {
  sourceProject: Option | null
  destinationProject: Option | null
}

type TransferHoursSidebarProps = {
  resize: boolean
  employeeId: string | undefined
  fullWidth?: boolean
}

const TransferHoursSidebar = ({ resize, employeeId, fullWidth = false }: TransferHoursSidebarProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { displayNotification } = useNotification()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = useState<boolean>(false)
  const handleOpen = () => setOpen(true)
  const date = useAppSelector(state => getSelectedDate(state))

  const resetProjectForm = () => {
    reset({ sourceProject: null, destinationProject: null })
    setProjectRangeStart(null)
    setProjectRangeEnd(null)
  }

  const resetDatesForm = () => {
    setOldRangeStart(null)
    setOldRangeEnd(null)
    setNewStartDate(null)
  }

  const handleClose = () => {
    setOpen(false)
    resetProjectForm()
    resetDatesForm()
  }

  const [tabIndex, setTabIndex] = useState<number>(0)
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => setTabIndex(newValue)
  const { control, getValues, reset, watch } = useForm<FormValues>({
    defaultValues: { sourceProject: null, destinationProject: null },
  })

  const [page, setPage] = useState(0)
  const [searchedTerm, setSearchedTerm] = useState('')
  const getAcronymProps = useCallback(() => {
    return { page: page, size: defaultPageSize, acronym: searchedTerm, loggable: true }
  }, [page, searchedTerm])
  const [acronymProps, setAcronymProps] = useState(getAcronymProps())
  const { data: projectsResponse } = useGetAcronymsQuery(acronymProps)
  const [projects, setProjects] = useState<ProjectAcronym[]>([])
  const onSelectBottomScroll = () => {
    if (projectsResponse && !projectsResponse.last) {
      setPage(prev => prev + 1)
    }
  }
  useEffect(() => {
    setAcronymProps(getAcronymProps())
  }, [getAcronymProps, page, searchedTerm])
  useEffect(() => {
    if (projectsResponse) {
      setProjects(prev => {
        const existingIds = new Set(prev.map(p => p.id))
        const newOptions = projectsResponse.content
          .filter((proj: any) => !existingIds.has(proj.id))
          .map((proj: any) => ({
            id: proj.id,
            acronym: proj.acronym,
            name: proj.name,
            projectStatus: proj.projectStatus,
            favorite: proj.favorite,
            label: proj.acronym,
            value: proj.id,
          }))
        return [...prev, ...newOptions]
      })
    }
  }, [projectsResponse])
  const projectOptions: Option[] = useMemo(() => {
    return projects.map(project => ({
      label: project.acronym,
      value: project.id,
    }))
  }, [projects])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const debouncedHandleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      setSearchedTerm(value)
    }, 500)
  }, [])

  const [projectRangeStart, setProjectRangeStart] = useState<Dayjs | null>(null)
  const [projectRangeEnd, setProjectRangeEnd] = useState<Dayjs | null>(null)
  const [transferProjectTrigger] = useTransferProjectMutation()

  const [oldRangeStart, setOldRangeStart] = useState<Dayjs | null>(null)
  const [oldRangeEnd, setOldRangeEnd] = useState<Dayjs | null>(null)
  const [newStartDate, setNewStartDate] = useState<Dayjs | null>(null)
  const [transferDatesTrigger] = useTransferDatesMutation()

  const handleSubmitProjectTransfer = async () => {
    const { sourceProject, destinationProject } = getValues()
    if (
      sourceProject &&
      destinationProject &&
      projectRangeStart &&
      projectRangeEnd &&
      sourceProject.value !== destinationProject.value
    ) {
      try {
        await transferProjectTrigger({
          employeeId,
          transfer: {
            sourceProjectId: sourceProject.value,
            destinationProjectId: destinationProject.value,
            startDate: projectRangeStart.format('YYYY-MM-DD'),
            endDate: projectRangeEnd.format('YYYY-MM-DD'),
          },
        }).unwrap()
        displayNotification({
          message: t('components.transferHours.projectTransfer.success'),
          type: 'success',
        })
        resetProjectForm()
      } catch (error) {
        const errorDescription = createErrorMessage(error)
        displayNotification({
          message: errorDescription || t('components.transferHours.projectTransfer.error'),
          type: 'error',
        })
      }
    }
  }
  const handleSubmitDatesTransfer = async () => {
    if (oldRangeStart && oldRangeEnd && newStartDate) {
      try {
        await transferDatesTrigger({
          employeeId,
          transfer: {
            oldStartDate: oldRangeStart.format('YYYY-MM-DD'),
            oldEndDate: oldRangeEnd.format('YYYY-MM-DD'),
            newStartDate: newStartDate.format('YYYY-MM-DD'),
          },
        }).unwrap()
        displayNotification({
          message: t('components.transferHours.dateTransfer.success'),
          type: 'success',
        })
        resetDatesForm()
      } catch (error) {
        const errorDescription = createErrorMessage(error)
        displayNotification({
          message: errorDescription || t('components.transferHours.dateTransfer.error'),
          type: 'error',
        })
      }
    }
  }
  const sourceProject = watch('sourceProject')
  const destinationProject = watch('destinationProject')
  const isProjectTransferDisabled =
    !sourceProject ||
    !destinationProject ||
    sourceProject.value === destinationProject.value ||
    !projectRangeStart ||
    !projectRangeEnd

  const isNewStartEqualOldStart: boolean =
    oldRangeStart && newStartDate ? newStartDate.isSame(oldRangeStart, 'day') : false
  const isDatesTransferDisabled: boolean = !oldRangeStart || !oldRangeEnd || !newStartDate || isNewStartEqualOldStart

  return (
    <>
      {resize ? (
        <Tooltip title={<Typography variant="caption">{t('components.tooltip.modifyMany')}</Typography>}>
          <span>
            <SquareButton aria-label={t('components.tooltip.modifyMany')} color="secondary" onClick={handleOpen}>
              <DynamicFeed />
            </SquareButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          startIcon={<DynamicFeed />}
          color="secondary"
          size="medium"
          onClick={handleOpen}
          fullWidth={fullWidth}
        >
          {t('components.tooltip.modify')}
        </Button>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { bgcolor: 'background.main', overflowX: 'hidden' }, elevation: 0 }}
      >
        <Stack px={isDownSm ? 3 : 6} pt={3} pb={3} width={isDownSm ? '350px' : '570px'}>
          <Button
            onClick={handleClose}
            variant="text"
            color="secondary"
            startIcon={<ArrowBack />}
            size="small"
            sx={{ position: 'absolute', ml: -3 }}
            aria-label={t('components.transferHours.return')}
          >
            {t('components.transferHours.return')}
          </Button>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{ pt: 4 }}
          >
            <Tab label={t('components.transferHours.projectTransfer.tab')} />
            <Tab label={t('components.transferHours.dateTransfer.tab')} />
          </Tabs>
          {tabIndex === 0 && (
            <Stack spacing={2} mt={3}>
              <AutocompleteInput
                options={projectOptions}
                onSelectBottomScroll={onSelectBottomScroll}
                name="sourceProject"
                control={control}
                label={t('components.transferHours.projectTransfer.sourceProject')}
                noOptionsText={t('components.transferHours.projectTransfer.noProjects')}
                listboxMaxHeight={250}
                handleInputChange={debouncedHandleInputChange}
                filterOptions={(options, state) => filterOptions(options, state, projects)}
              />
              <AutocompleteInput
                options={projectOptions}
                onSelectBottomScroll={onSelectBottomScroll}
                name="destinationProject"
                control={control}
                label={t('components.transferHours.projectTransfer.destinationProject')}
                noOptionsText={t('components.transferHours.projectTransfer.noProjects')}
                listboxMaxHeight={250}
                handleInputChange={debouncedHandleInputChange}
                filterOptions={(options, state) => filterOptions(options, state, projects)}
              />
              <DateRangePickerWithPresets
                startDate={projectRangeStart}
                endDate={projectRangeEnd}
                setStartDate={setProjectRangeStart}
                setEndDate={setProjectRangeEnd}
                startDateLabel={t('components.transferHours.projectTransfer.dateRangeFrom')}
                endDateLabel={t('components.transferHours.projectTransfer.dateRangeTo')}
                suggestedDate={dayjs(date)}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmitProjectTransfer}
                disabled={isProjectTransferDisabled}
              >
                {t('components.transferHours.projectTransfer.transfer')}
              </Button>
            </Stack>
          )}
          {tabIndex === 1 && (
            <Stack spacing={2} mt={3}>
              <DateRangePickerWithPresets
                startDate={oldRangeStart}
                endDate={oldRangeEnd}
                setStartDate={setOldRangeStart}
                setEndDate={setOldRangeEnd}
                startDateLabel={t('components.transferHours.dateTransfer.dateRangeFrom')}
                endDateLabel={t('components.transferHours.dateTransfer.dateRangeTo')}
                suggestedDate={dayjs(date)}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
                <DatePicker
                  label={t('components.transferHours.dateTransfer.newDate')}
                  value={newStartDate}
                  onChange={newDate => setNewStartDate(newDate)}
                  referenceDate={dayjs(date) || dayjs()}
                />
              </LocalizationProvider>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmitDatesTransfer}
                disabled={isDatesTransferDisabled}
              >
                {t('components.transferHours.dateTransfer.transfer')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Drawer>
    </>
  )
}

export default TransferHoursSidebar
